// 任务统计：日 / 周 / 月切换 + 时间范围（快捷 / 自定义）+ 类型多选 + 客户关键字（全部写在 URL）
// → 4 张统计卡、柱状图（新建 / 完成 / 卡住·失败）、明细表、按类型 / 按客户排行（点一行跳到「任务」列表并带上筛选）
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { fmtDur } from "../lib/format";
import { useLoad } from "../lib/hooks";
import type { StatsBucket, StatsCounts, TaskStats } from "../lib/types";
import {
  DEFAULT_PRESET,
  EMPTY_FILTERS,
  RANGE_PRESETS,
  filtersToParams,
  parseStatsFilters,
  presetRange,
  sgToday,
  statsFiltersToParams,
  type Granularity,
  type StatsFilters,
} from "../lib/taskQuery";
import { PageHeading } from "../components/PageHeading";
import { ColumnPicker, DataTable, useHiddenColumns, type Column } from "../components/DataTable";
import { MultiSelectFilter, type Option } from "../components/MultiSelectFilter";
import { StatsChart } from "../components/StatsChart";

const G_TEXT: Record<Granularity, string> = { day: "日", week: "周", month: "月" };
const AVG = (ms: number | null) => (ms === null ? "—" : fmtDur(ms));

const BUCKET_COLUMNS: Column<StatsBucket>[] = [
  { key: "label", label: "时间段", fixed: true, render: (b) => b.label },
  { key: "created", label: "新建", render: (b) => num(b.created) },
  { key: "done", label: "完成", render: (b) => num(b.done) },
  { key: "troubled", label: "卡住/失败", tone: (b) => (b.troubled ? "cell-warn" : undefined), render: (b) => num(b.troubled) },
  { key: "avg", label: "平均用时", render: (b) => (b.avgMs === null ? <span className="muted">—</span> : fmtDur(b.avgMs)) },
];

function num(n: number) {
  return n ? n : <span className="muted">0</span>;
}

/** 排行的三列数字（按类型 / 按客户共用） */
function countCols<T extends StatsCounts>(): Column<T>[] {
  return [
    { key: "created", label: "新建", fixed: true, render: (r) => num(r.created) },
    { key: "done", label: "完成", fixed: true, render: (r) => num(r.done) },
    { key: "troubled", label: "卡住失败", fixed: true, render: (r) => num(r.troubled) },
  ];
}
type TypeRow = TaskStats["byType"][number];
type ClientRow = TaskStats["byClient"][number];
const TYPE_COLUMNS: Column<TypeRow>[] = [
  { key: "name", label: "类型", fixed: true, render: (r) => <b className="cell-clip" title={r.name}>{r.name}</b> },
  ...countCols<TypeRow>(),
];
const CLIENT_COLUMNS: Column<ClientRow>[] = [
  {
    key: "name",
    label: "客户",
    fixed: true,
    render: (r) => (
      <div className="cell-two">
        <b title={r.name} translate={r.name ? "no" : undefined}>{r.name || "（未填公司名）"}</b>
        <span>{r.uen ? `UEN ${r.uen}` : "无 UEN"}</span>
      </div>
    ),
  },
  ...countCols<ClientRow>(),
];

function useStatsFilters(): [StatsFilters, (next: StatsFilters) => void] {
  const [params, setParams] = useSearchParams();
  const today = sgToday();
  const f = useMemo(() => parseStatsFilters(params, today), [params, today]);
  return [f, (next) => setParams(statsFiltersToParams(next), { replace: true })];
}

export function TaskStatsPage() {
  const navigate = useNavigate();
  const [f, setF] = useStatsFilters();
  const [kw, setKw] = useState(f.q);
  const [hidden, toggleCol] = useHiddenColumns("csp-hidden-columns-stats");
  const types = useLoad(() => api.types(true), []);
  useEffect(() => setKw(f.q), [f.q]);

  const badRange = f.from > f.to;
  const apiQuery = useMemo(() => {
    const p = new URLSearchParams({ granularity: f.g, from: f.from, to: f.to });
    if (f.typeId.length) p.set("typeId", f.typeId.join(","));
    if (f.q) p.set("q", f.q);
    return p.toString();
  }, [f]);
  const stats = useLoad(async () => (badRange ? null : api.taskStats(new URLSearchParams(apiQuery))), [apiQuery, badRange]);
  const data = stats.data;

  const typeOptions: Option[] = (types.data ?? []).map((t) => ({ value: t.id, label: t.name }));
  for (const id of f.typeId) if (!typeOptions.some((o) => o.value === id)) typeOptions.push({ value: id, label: "（其他类型）" });

  const setG = (g: Granularity) => setF({ ...f, g, preset: DEFAULT_PRESET[g], ...presetRange(g, DEFAULT_PRESET[g], sgToday()) });
  const setPreset = (v: string) => {
    if (v === "custom") setF({ ...f, preset: null });
    else setF({ ...f, preset: Number(v), ...presetRange(f.g, Number(v), sgToday()) });
  };

  /** 跳到「任务」列表：同一时间范围（按创建日期）+ 额外筛选 */
  const openList = (extra: { typeId?: string[]; q?: string }) => {
    const from = data?.buckets[0]?.start ?? f.from;
    const to = data?.to ?? f.to;
    const p = filtersToParams({ ...EMPTY_FILTERS, typeId: extra.typeId ?? f.typeId, q: extra.q ?? f.q, from, to });
    navigate(`/?${p.toString()}`);
  };

  const empty = data && data.totals.created + data.totals.done + data.totals.troubled === 0;

  return (
    <div>
      <PageHeading title="任务统计" desc="按日 / 周 / 月看新建、完成、卡住失败的任务数和平均用时（新加坡时间）" />

      <div className="card">
        <div className="filter-row" data-testid="stats-filters">
          <div className="seg" role="group" aria-label="统计粒度" data-testid="granularity">
            {(["day", "week", "month"] as Granularity[]).map((g) => (
              <button key={g} type="button" className={f.g === g ? "active" : undefined} aria-pressed={f.g === g} onClick={() => setG(g)}>
                按{G_TEXT[g]}
              </button>
            ))}
          </div>
          <select value={f.preset ?? "custom"} onChange={(e) => setPreset(e.target.value)} aria-label="时间范围" data-testid="range-select">
            {RANGE_PRESETS[f.g].map((p) => (
              <option key={p.n} value={p.n}>
                {p.label}
              </option>
            ))}
            <option value="custom">自定义…</option>
          </select>
          {f.preset === null && (
            <label className="row date-range">
              <input type="date" value={f.from} max={f.to} onChange={(e) => e.target.value && setF({ ...f, from: e.target.value })} aria-label="开始日期" />
              <span className="muted">–</span>
              <input type="date" value={f.to} min={f.from} onChange={(e) => e.target.value && setF({ ...f, to: e.target.value })} aria-label="结束日期" />
            </label>
          )}
          <MultiSelectFilter label="类型" options={typeOptions} value={f.typeId} onChange={(v) => setF({ ...f, typeId: v })} testId="stats-type-filter" />
          <form
            className="row"
            onSubmit={(e) => {
              e.preventDefault();
              setF({ ...f, q: kw.trim() });
            }}
          >
            <input type="search" placeholder="客户名称 / UEN" value={kw} onChange={(e) => setKw(e.target.value)} aria-label="客户关键字" />
            <button className="btn">搜索</button>
          </form>
        </div>
        <div className="muted small" data-testid="stats-range">
          {f.from} 至 {f.to}
          {" · "}口径：新建按创建时间；完成按变成「完成」的时间；卡住/失败 = 该段进入卡住、失败或取消的任务数（同一任务同段算一次）；平均用时 = 该段完成任务从开始执行到完成的时长
        </div>
      </div>

      {badRange && <div className="notice err">开始日期不能晚于结束日期</div>}
      {stats.error && <div className="notice err">{stats.error}</div>}
      {stats.loading && !data && <div className="empty">正在统计…</div>}

      {data && (
        <>
          <div className="stat-cards" data-testid="stats-cards">
            <StatCard label="新建" value={data.totals.created} tone="brand" testId="total-created" />
            <StatCard label="完成" value={data.totals.done} tone="ok" testId="total-done" />
            <StatCard label="卡住 / 失败 / 取消" value={data.totals.troubled} tone="danger" testId="total-troubled" />
            <StatCard label="平均用时（完成的任务）" value={AVG(data.totals.avgMs)} testId="total-avg" />
          </div>

          {empty ? (
            <div className="card">
              <div className="empty" data-testid="stats-empty">
                这段时间内没有任务。换个时间范围，或清掉类型 / 客户筛选再看看。
              </div>
            </div>
          ) : (
            <>
              <div className="card">
                <h2>按{G_TEXT[data.granularity]}趋势</h2>
                <StatsChart buckets={data.buckets} />
              </div>

              <div className="grid grid-2 rank-grid">
                <div className="card">
                  <h2>按类型（前 10）</h2>
                  <p className="muted small">点一行查看这个类型在本时间范围内创建的任务</p>
                  <DataTable
                    columns={TYPE_COLUMNS}
                    rows={data.byType}
                    rowKey={(r) => r.typeId}
                    onRowClick={(r) => openList({ typeId: [r.typeId] })}
                    rowAttrs={(r) => ({ "data-testid": "rank-type", "data-type-id": r.typeId })}
                    empty="没有数据"
                  />
                </div>
                <div className="card">
                  <h2>按客户（前 10）</h2>
                  <p className="muted small">点一行查看这个客户在本时间范围内创建的任务</p>
                  <DataTable
                    columns={CLIENT_COLUMNS}
                    rows={data.byClient}
                    rowKey={(r) => `${r.uen}|${r.name}`}
                    onRowClick={(r) => openList({ q: r.uen || r.name })}
                    rowAttrs={(r) => ({ "data-testid": "rank-client", "data-uen": r.uen })}
                    empty="没有数据"
                  />
                </div>
              </div>

              <div className="card">
                <div className="list-bar">
                  <span className="count">
                    明细 · 共 {data.buckets.length} 段（按{G_TEXT[data.granularity]}，最近的在上）
                  </span>
                  <ColumnPicker columns={BUCKET_COLUMNS} hidden={hidden} onToggle={toggleCol} />
                </div>
                <DataTable
                  columns={BUCKET_COLUMNS}
                  rows={[...data.buckets].reverse()}
                  rowKey={(b) => b.key}
                  hidden={hidden}
                  rowAttrs={(b) => ({ "data-testid": "bucket-row", "data-key": b.key })}
                />
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, tone, testId }: { label: string; value: number | string; tone?: string; testId: string }) {
  const zero = value === 0 || value === "—";
  return (
    <div className={`stat-card static ${zero ? "zero" : (tone ?? "")}`} data-testid={testId}>
      <span className="num">{value}</span>
      <span className="lbl">{label}</span>
    </div>
  );
}
